import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {ButtonIcon} from '../icon/button-icon';

import type {ButtonVariant, ButtonSize} from './types';

import styles from './button.module.css';

// Separate style props because Button wrapper components may not have children.
export type ButtonStyleProps = {
    variant?: ButtonVariant;
    size?: ButtonSize;
    as?: 'button' | 'div';
    isFullWidth?: boolean;
    wiggle?: boolean;
    getButtonRef?: any;
    icon?: React.ComponentType<any>;
    iconColor?: string;
    noPadding?: boolean; // sometimes you just want a button with NO padding,
    tooltipText?: string;
    fitContent?: boolean;
    minWidthFitContent?: boolean; // for when you want the button to never be narrower than its content,
    preventUnderline?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    noBorder?: boolean;
    overflowHidden?: boolean;
    alwaysUnderline?: boolean;
}; // Exact type because cannot contain children

type Props = {
    children: React.ReactNode;
    className?: string;
} & ButtonStyleProps &
    React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
This is the "base" Nutshell button component, which gives us a consistent way of rendering
a button element in our app. We have 5 main "variants" of this button, which just change its colors.

There are also 4 "sizes", which determine the width/height/padding and text size of the button.

It is up to the component using the Button to provide the text contents via `children`.
 */
export function Button(props: Props) {
    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    });

    const {
        variant = 'secondary',
        size = 'normal',
        as = 'button',
        isFullWidth,
        wiggle = false,
        noPadding,
        children,
        getButtonRef,
        icon,
        iconColor,
        tooltipText,
        fitContent,
        minWidthFitContent,
        preventUnderline,
        textAlign = 'center',
        noBorder = false,
        overflowHidden = false,
        alwaysUnderline = false,
        className,
        ...htmlButtonProps
    } = props;

    const styleName = classnames(
        styles[variant],
        styles[size],
        {
            [styles['full-width']]: isFullWidth,
        },
        {
            [styles['no-padding']]: noPadding,
        },
        {
            [styles['fit-content']]: fitContent,
        },
        {
            [styles['min-width-fit-content']]: minWidthFitContent,
        },
        {
            [styles['prevent-underline']]: preventUnderline,
        },
        {
            [styles.wiggle]: wiggle,
        },
        {
            [styles['text-align-center']]: textAlign === 'center',
        },
        {
            [styles['text-align-left']]: textAlign === 'left',
        },
        {
            [styles['text-align-right']]: textAlign === 'right',
        },
        {
            [styles['no-border']]: noBorder,
        },
        {
            [styles['overflow-hidden']]: overflowHidden,
        },
        {
            [styles['always-underline']]: alwaysUnderline,
        }
    );

    // Allows us to render a button inside another button without errors
    const Component = as;

    return (
        <Component
            type='button'
            ref={getButtonRef}
            data-tip={tooltipText}
            data-type={
                variant === 'text-primary' || variant === 'text-secondary' ? 'link' : undefined
            }
            {...(htmlButtonProps as React.HTMLAttributes<HTMLElement>)}
            className={classnames(styleName, className)}
        >
            {icon ? (
                <ButtonIcon size={size} variant={variant} icon={icon} fillColor={iconColor} />
            ) : undefined}
            {children}
        </Component>
    );
}
